import type { Webhook } from "@repo/shared/types";
import { Pencil, ScrollText, Send, Trash2 } from "lucide-react";
import { useState, useTransition } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useConfirm } from "@/hooks/use-confirm";
import { deleteWebhook, testWebhook } from "@/lib/api/webhooks";
import { useT } from "@/lib/i18n/use-t";
import type { WebhookEventType } from "./webhook-events";
import { WebhookLogsDialog } from "./webhook-logs-dialog";
import { EVENTS, T } from "./webhooks-table.i18n";

type WebhooksTableProps = {
  webhooks: Webhook[];
  onEdit: (webhook: Webhook) => void;
  mutate: () => void;
};

export function WebhooksTable({ webhooks, onEdit, mutate }: WebhooksTableProps) {
  const eventsT = useT(EVENTS);
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [logsWebhook, setLogsWebhook] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  const handleDelete = async (id: string) => {
    startTransition(async () => {
      try {
        await deleteWebhook(id);
        mutate();
      } catch (err) {
        await confirm({
          title: t.error,
          description: err instanceof Error ? err.message : t.failedToDeleteWebhook,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
      setDeleteId(null);
    });
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      await testWebhook(id);
      setTestingId(null);
      await confirm({
        title: t.testSent,
        description: t.testSuccessDescription,
        confirmText: t.ok,
        type: "info",
      });
    } catch (err) {
      setTestingId(null);
      await confirm({
        title: t.error,
        description: err instanceof Error ? err.message : t.failedToSendTestWebhook,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  if (webhooks.length === 0) {
    return <EmptyState>{t.noWebhooksYet}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.url}</TableHead>
            <TableHead>{t.events}</TableHead>
            <TableHead>{t.status}</TableHead>
            <TableHead className="text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {webhooks.map((webhook) => (
            <TableRow key={webhook.id}>
              <TableCell className="font-medium">{webhook.name}</TableCell>
              <TableCell className="max-w-xs truncate font-mono text-xs">{webhook.url}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {webhook.events.slice(0, 2).map((event) => (
                    <Badge key={event} variant="secondary" className="text-xs">
                      {eventsT[event as WebhookEventType] ?? event}
                    </Badge>
                  ))}
                  {webhook.events.length > 2 && (
                    <Badge variant="secondary" className="text-xs">
                      +{webhook.events.length - 2} {t.more}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {webhook.isActive ? (
                  <Badge variant="default">{t.active}</Badge>
                ) : webhook.disabledReason ? (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="destructive">{t.autoDisabled}</Badge>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">{webhook.disabledReason}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Badge variant="secondary">{t.inactive}</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setLogsWebhook({ id: webhook.id, name: webhook.name })}
                    tooltip={t.logs}
                  >
                    <ScrollText className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleTest(webhook.id)}
                    disabled={!webhook.isActive}
                    tooltip={t.test}
                    loading={testingId === webhook.id}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(webhook)}
                    tooltip={t.edit}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteId(webhook.id)}
                    disabled={isPending}
                    tooltip={t.deleteWebhook}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteWebhook}</AlertDialogTitle>
            <AlertDialogDescription>{t.deleteConfirmation}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDelete(deleteId)}
              disabled={isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isPending ? t.deleting : t.deleteWebhookAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WebhookLogsDialog
        open={logsWebhook !== null}
        webhookId={logsWebhook?.id || null}
        webhookName={logsWebhook?.name || ""}
        onClose={() => setLogsWebhook(null)}
      />
      <ConfirmationDialog />
    </>
  );
}
